import { FunctionComponent, useEffect } from 'react';
import getEligibilityCheckAsync from '../../api/eligibility/getEligibilityCheckAsync';
import {
  AffiliationType,
  EligibilityCheckResponse,
} from '../../interfaces/register';

interface Props {
  eligibilityToken: string;
  onEligible(
    affiliationType: AffiliationType,
    affiliationId: number,
    existingAccount?: boolean
  ): Promise<any>;
  onIneligible(eligibilityCheckId: string): Promise<any>;
  onComplete(): void;
  onFailed?(): void;
  // The container injects the guest poll fn for the coverage-check flow; default is the
  // authed poll so this component stays pure.
  pollEligibilityCheck?(
    id: string
  ): Promise<{ eligibilityCheck: EligibilityCheckResponse | undefined }>;
}

const EligibilityPoll: FunctionComponent<Props> = ({
  eligibilityToken,
  onEligible,
  onIneligible,
  onComplete,
  onFailed,
  pollEligibilityCheck = getEligibilityCheckAsync,
}: Props) => {
  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const { eligibilityCheck } = await pollEligibilityCheck(
          eligibilityToken
        );
        // Both poll fns catch API errors internally and return undefined rather than throwing.
        // undefined means the fetch failed (non-2xx or parse error) — not "still pending"
        // (a pending check returns the object with completed: false). Treat it as failed.
        if (!eligibilityCheck) {
          clearInterval(timer);
          if (onFailed) onFailed();
          return;
        }
        if (eligibilityCheck.completed) {
          if (
            eligibilityCheck.eligible &&
            eligibilityCheck.groupList &&
            eligibilityCheck.groupList.length === 0
          ) {
            onEligible(
              eligibilityCheck.affiliationType,
              eligibilityCheck.affiliationId,
              eligibilityCheck.existingAccount
            );
          } else {
            onIneligible(eligibilityCheck.token);
          }
        }
      } catch {
        // Guard for unexpected throws (programming errors); the normal error path is the
        // undefined check above.
        clearInterval(timer);
        if (onFailed) onFailed();
      }
    }, 750); // @TODO: should we cancel this after X number of retries?
    return () => {
      onComplete();
      clearInterval(timer);
    };
  }, []);

  return null;
};

export default EligibilityPoll;
